import React from 'react'
import { Company } from 'models'
import { ALL_ACCOUNTS_ID, MAX_SELECTED_CHILDREN } from 'contexts/CompaniesContext'
import type { SelectionSummary } from '../types/selectionTypes'
import { useValidationHint } from './useValidationHint'

type ChildSelectorBehaviorParams = {
  childrenCompanies: Array<Company>
  isOrphanSelected: boolean
  pendingSelection: Array<Company>
  validatedSelectionItems: Array<Company>
  isAllAccountsSelected: boolean
  isChildSelectorDisabled: boolean
  pendingSelectionLimitReached: boolean
  hasPendingSelection: boolean
  selectionSummary: SelectionSummary
}

const useChildSelectorBehavior = ({
  childrenCompanies,
  isOrphanSelected,
  pendingSelection,
  validatedSelectionItems,
  isAllAccountsSelected,
  isChildSelectorDisabled,
  pendingSelectionLimitReached,
  hasPendingSelection,
  selectionSummary,
}: ChildSelectorBehaviorParams) => {
  const [isChildDropdownOpen, setIsChildDropdownOpen] = React.useState(false)
  const [isHoveringChildInput, setIsHoveringChildInput] = React.useState(false)
  const [showValidationHint, setShowValidationHint] = useValidationHint(hasPendingSelection, isChildDropdownOpen)
  const autoOpenHandledDatasetRef = React.useRef<string | null>(null)
  const lastDatasetKeyRef = React.useRef<string>('')
  const childInputRef = React.useRef<HTMLInputElement | null>(null)

  const childDatasetKey = React.useMemo(
    () => childrenCompanies.map((company) => company.id).join(','),
    [childrenCompanies],
  )
  const actualChildrenCount = React.useMemo(
    () => childrenCompanies.filter((company) => company.id !== ALL_ACCOUNTS_ID).length,
    [childrenCompanies],
  )

  React.useEffect(() => {
    if (lastDatasetKeyRef.current !== childDatasetKey) {
      lastDatasetKeyRef.current = childDatasetKey
      autoOpenHandledDatasetRef.current = null
    }

    const shouldAutoOpenDropdown =
      Boolean(childDatasetKey) &&
      !isOrphanSelected &&
      actualChildrenCount > MAX_SELECTED_CHILDREN &&
      pendingSelection.length === 0 &&
      validatedSelectionItems.length === 0 &&
      !isAllAccountsSelected &&
      !isChildSelectorDisabled &&
      autoOpenHandledDatasetRef.current === null

    if (shouldAutoOpenDropdown) {
      autoOpenHandledDatasetRef.current = childDatasetKey
      setIsChildDropdownOpen(true)
    }
  }, [
    childDatasetKey,
    actualChildrenCount,
    isOrphanSelected,
    pendingSelection.length,
    validatedSelectionItems.length,
    isAllAccountsSelected,
    isChildSelectorDisabled,
  ])

  React.useEffect(() => {
    if (isChildSelectorDisabled) {
      setIsChildDropdownOpen(false)
    }
  }, [isChildSelectorDisabled])

  React.useEffect(() => {
    if (isChildDropdownOpen && childInputRef.current) {
      childInputRef.current.focus()
    }
  }, [isChildDropdownOpen])

  const shouldShowSelectionTooltip = Boolean(
    selectionSummary.type !== 'none' && isHoveringChildInput && !isChildDropdownOpen,
  )
  const shouldShowLimitTooltip = pendingSelectionLimitReached && isChildDropdownOpen

  return {
    isChildDropdownOpen,
    openDropdown: () => setIsChildDropdownOpen(true),
    closeDropdown: () => setIsChildDropdownOpen(false),
    onHoverChildInput: setIsHoveringChildInput,
    childInputRef,
    shouldShowSelectionTooltip,
    shouldShowLimitTooltip,
    showValidationHint,
    setShowValidationHint,
  }
}

export type { ChildSelectorBehaviorParams }
export { useChildSelectorBehavior }
